

import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Float, OrbitControls, Box, Sphere, Torus, Text3D, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { Typewriter } from 'react-simple-typewriter';

// ✅ Fast, clean Typewriter component
const TypewriterEffect = ({
  text,
  fontSize = '2.8rem',
  fontColor = '#fff',
  backgroundColor = 'transparent',
}) => {
  return (
    <div
      style={{
        fontSize,
        color: fontColor,
        backgroundColor,
        whiteSpace: 'pre-wrap',
        fontFamily: 'monospace',
      }}
    >
      <span>
        <Typewriter
          words={[text]}
          loop={1}
          cursor
          cursorStyle="|"
          typeSpeed={15}     // ⚡ Fast speed
          deleteSpeed={0}
          delaySpeed={500}
        />
      </span>
    </div>
  );
};

// 🌌 Floating 3D Elements
const FloatingElements = () => {
  const groupRef = useRef();

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.1;
      groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.15) * 0.1;
    }
  });

  return (
    <group ref={groupRef}>
      <Float speed={2} rotationIntensity={0.8} floatIntensity={0.6}>
        <Box args={[0.6, 0.6, 0.6]} position={[-4, 3, 0]}>
          <meshStandardMaterial color="#007bff" transparent opacity={0.8} metalness={0.3} roughness={0.2} />
        </Box>
      </Float>
      <Float speed={1.8} rotationIntensity={1.2} floatIntensity={0.7}>
        <Box args={[0.4, 0.4, 0.4]} position={[4, -2, 3]}>
          <meshStandardMaterial color="#ff6b35" transparent opacity={0.9} metalness={0.5} roughness={0.1} />
        </Box>
      </Float>
      <Float speed={2.8} rotationIntensity={0.5} floatIntensity={0.5}>
        <Box args={[0.5, 0.5, 0.5]} position={[0, 4, -3]}>
          <meshStandardMaterial color="#28a745" transparent opacity={0.7} metalness={0.2} roughness={0.3} />
        </Box>
      </Float>
      <Float speed={2.2} rotationIntensity={0.9} floatIntensity={0.8}>
        <Sphere args={[0.25, 32, 32]} position={[-3, -3, 2]}>
          <meshStandardMaterial color="#ffc107" emissive="#ffc107" emissiveIntensity={0.5} metalness={0.8} roughness={0.1} />
        </Sphere>
      </Float>
      <Float speed={2.5} rotationIntensity={0.6} floatIntensity={0.6}>
        <Sphere args={[0.2, 32, 32]} position={[3, 2, -2]}>
          <meshStandardMaterial color="#dc3545" emissive="#dc3545" emissiveIntensity={0.6} metalness={0.7} roughness={0.2} />
        </Sphere>
      </Float>
      <Float speed={2.1} rotationIntensity={1.0} floatIntensity={0.7}>
        <Torus args={[0.4, 0.15, 24, 100]} position={[0, 0, 4]}>
          <meshStandardMaterial color="#6f42c1" transparent opacity={0.9} metalness={0.6} roughness={0.1} />
        </Torus>
      </Float>
      {[...Array(30)].map((_, i) => (
        <Float key={i} speed={1.5 + Math.random() * 2.5} rotationIntensity={0.8} floatIntensity={0.4}>
          <Sphere
            args={[0.03 + Math.random() * 0.04, 16, 16]}
            position={[(Math.random() - 0.5) * 12, (Math.random() - 0.5) * 12, (Math.random() - 0.5) * 12]}
          >
            <meshStandardMaterial
              color={`hsl(${Math.random() * 360}, 80%, 70%)`}
              emissive={`hsl(${Math.random() * 360}, 80%, 70%)`}
              emissiveIntensity={0.7}
              metalness={0.9}
              roughness={0.1}
            />
          </Sphere>
        </Float>
      ))}
    </group>
  );
};

// 🌀 Animated 3D Text
const AnimatedText3D = () => {
  const textRef = useRef();
  useFrame((state) => {
    if (textRef.current) {
      textRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
      textRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });

  return (
    <Float speed={1} rotationIntensity={0.2} floatIntensity={0.3}>
      <group ref={textRef} position={[0, 0, 0]}>
        <Text3D
          font="/fonts/helvetiker_regular.typeface.json"
          size={0.5}
          height={0.1}
          curveSegments={12}
          bevelEnabled
          bevelThickness={0.02}
          bevelSize={0.02}
          bevelOffset={0}
          bevelSegments={5}
        >
          ZYNTRO
          <meshStandardMaterial
            color="#007bff"
            emissive="#007bff"
            emissiveIntensity={0.2}
            transparent
            opacity={0.9}
          />
        </Text3D>
      </group>
    </Float>
  );
};

// 🌠 Main 3D Scene
const Scene3D = () => {
  const { scene } = useThree();
  useEffect(() => {
    scene.fog = new THREE.Fog('#000000', 2, 25);
  }, [scene]);

  return (
    <>
      <ambientLight intensity={0.3} />
      <directionalLight position={[10, 10, 5]} intensity={1.2} />
      <pointLight position={[-10, -10, -5]} intensity={0.8} color="#007bff" />
      <pointLight position={[10, -10, -5]} intensity={0.8} color="#ff6b35" />
      <pointLight position={[0, 10, 0]} intensity={0.6} color="#28a745" />
      <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
      <FloatingElements />
      <AnimatedText3D />
    </>
  );
};

// 🌍 Final Page Component
const BuildAutomateGrowPage = () => {
  return (
    <div
      style={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
      }}
    >
      {/* 3D Canvas Background */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
      >
        <Canvas
          camera={{ position: [0, 0, 10], fov: 70 }}
          gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
        >
          <Scene3D />
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            autoRotate
            autoRotateSpeed={0.3}
            maxPolarAngle={Math.PI / 2.2}
            minPolarAngle={Math.PI / 2.2}
            enableDamping
            dampingFactor={0.05}
          />
        </Canvas>
      </div>

      {/* Typewriter Text Overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'radial-gradient(circle at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)',
          backdropFilter: 'blur(3px)',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.7, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.5, type: 'spring', stiffness: 80, damping: 15 }}
          style={{
            textAlign: 'center',
            color: '#ffffff',
            textShadow: '0 6px 12px rgba(0,0,0,0.8), 0 0 20px rgba(0,123,255,0.3)',
            maxWidth: '90vw',
            padding: '2rem',
          }}
        >
          <TypewriterEffect
            text="Build, Automate, Grow : The Future Starts with Zyntro"
            fontSize="2.8rem"
            fontColor="#fff"
            backgroundColor="transparent"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default BuildAutomateGrowPage;
